import { useContext, useEffect } from 'react'
import { PlaylistContext } from '../components/Context/PlaylistContext'
import { FetchPlaylistResponse } from '../swagger'

/**
 *
 * usePlaylistContext
 * Custom hook to access and manage a playlist by its `playlistKey`.
 *
 * It:
 * - Retrieves the playlist from context.
 * - Automatically triggers a fetch if the playlist is not loaded or mismatched.
 * - Provides `isLoading` and a manual `refetch` function.
 *
 * Usage Note:
 * - `refetch` should be used only when you explicitly want to re-fetch the playlist data.
 *
 * Params:
 * - playlistKey (number): The unique identifier of the playlist to fetch.
 *
 * Returns:
 * - playlist: The current playlist data (if loaded).
 * - isLoading: Boolean indicating if the playlist is currently being fetched.
 * - refetch: Function to manually re-fetch the playlist.
 **/
export const usePlaylistContext = ({
  playlistKey,
}: {
  playlistKey: number
}): {
  playlist?: FetchPlaylistResponse
  isLoading: boolean
  refetch: () => void
  updatePlaylist: (value: FetchPlaylistResponse) => void
} => {
  const { playlist, isLoading, loadPlaylist, refetch, updatePlaylist } =
    useContext(PlaylistContext)

  useEffect(() => {
    if (!playlist || playlist.playlist?.playlistKey !== playlistKey) {
      loadPlaylist(playlistKey)
    }
  }, [playlistKey, playlist, loadPlaylist])

  return { playlist, isLoading, refetch, updatePlaylist }
}
